import {
  DARK_SCHEME_QUERY,
  type PreferredTheme,
  readThemePreference,
  type ThemePreference,
  themePreferenceCookie,
} from './preference.ts';

/** Where the preference is kept. Injectable so the provider can be tested and shown without cookies. */
export interface ThemePreferenceStore {
  read: () => ThemePreference;
  write: (preference: ThemePreference) => void;
}

/** The `wh-theme` cookie on `domain`, the environment's session cookie domain. */
export const documentThemePreferenceStore = (domain: string, doc: Document = document): ThemePreferenceStore => ({
  read: () => readThemePreference(doc.cookie),
  write: (preference) => {
    doc.cookie = themePreferenceCookie(preference, domain);
  },
});

/** A preference held in memory, for showcases and tests. */
export const memoryThemePreferenceStore = (initial: ThemePreference = 'light'): ThemePreferenceStore => {
  let preference = initial;
  return {
    read: () => preference,
    write: (next) => {
      preference = next;
    },
  };
};

/** Whether the device prefers a dark colour scheme, and a way to hear when that changes. */
export interface ColorSchemeSource {
  prefersDark: () => boolean;
  /** Calls `onChange` whenever the setting changes; returns the unsubscribe. */
  subscribe: (onChange: () => void) => () => void;
}

/** The device's setting through `matchMedia`. Without `matchMedia`, light and unchanging. */
export const mediaColorSchemeSource = (
  matchMedia: () => Window['matchMedia'] | undefined = () => globalThis.window?.matchMedia?.bind(globalThis.window),
): ColorSchemeSource => {
  const query = () => matchMedia()?.(DARK_SCHEME_QUERY);

  return {
    prefersDark: () => query()?.matches ?? false,
    subscribe: (onChange) => {
      const list = query();
      list?.addEventListener('change', onChange);
      return () => list?.removeEventListener('change', onChange);
    },
  };
};

/** The attribute the boot script and the provider put on `<html>`, so tokens.scss paints the canvas in the theme. */
export const DOCUMENT_THEME_ATTRIBUTE = 'data-wh-theme';

export const applyDocumentTheme = (theme: PreferredTheme, doc: Document = document): void => {
  doc.documentElement.setAttribute(DOCUMENT_THEME_ATTRIBUTE, theme);
};
